// Core
import {
  Injectable,
  Inject,
  ApplicationRef,
  ComponentFactoryResolver,
  Injector,
  ComponentRef,
  ReflectiveInjector,
  Provider,
  Type,
  NgZone
} from '@angular/core';
import { DOCUMENT } from '@angular/platform-browser';

// Entities
import { IImplicitContext, IPopupOptions, POPUP_NODE_CLASS } from './entities';

// Options
import { PopupOptions } from './popup.options';
import { Popup } from './popup';

// Component
import { PopupComponent } from './popup.component';

@Injectable()
export class PopupManager {
  private _overlayNode: HTMLElement;
  private _overlayRemoveTimeout: any;

  constructor(
    private _applicationRef: ApplicationRef,
    private _componentFactoryResolver: ComponentFactoryResolver,
    private _injector: Injector,
    private ngZone: NgZone,
    @Inject(DOCUMENT) private document,
    private options: PopupOptions
  ) {}

  // Create Dynamic Popup Component
  private createPopupComponent<T>(type: Type<T>, providers: any = []): ComponentRef<T> {
    const factory = this._componentFactoryResolver.resolveComponentFactory(type as Type<T>);
    const _providers = ReflectiveInjector.resolve(providers);
    const injector = ReflectiveInjector.fromResolvedProviders(_providers, this._injector);
    let popupNode = document.createElement('div');
    popupNode.id = 'popup-boxes-node-' + Math.floor(Math.random() * 200);
    popupNode.className = POPUP_NODE_CLASS;
    this.document.querySelector('body').appendChild(popupNode);
    const componentRef = factory.create(injector, [], popupNode);
    this.attachToApplication(componentRef);
    return componentRef;
  }

  // Inserts the component in the root application node.
  private attachToApplication<T>(componentRef: ComponentRef<T>): void {
    this._applicationRef.attachView(componentRef.hostView);
  }

  // Create Popup Window
  public createPopup(
    popup: Popup,
    options?: IPopupOptions
  ): Promise<{ componentRef: ComponentRef<PopupComponent>; popup: Popup }> {
    return new Promise(resolve => {
      let config: any = Object.assign({}, this.options, options || {});
      popup.options = config;
      this.createPopupOverlay(popup);
      let componentRef = this.createPopupComponent<PopupComponent>(PopupComponent);
      resolve({
        componentRef: componentRef,
        popup: popup
      });
    });
  }

  // Create Popup Overlay
  public createPopupOverlay(popup: Popup): void {
    if (this._overlayRemoveTimeout) {
      clearTimeout(this._overlayRemoveTimeout);
      this._overlayRemoveTimeout = void 0;
    }
    if (!this._overlayNode && popup.options.showOverlay) {
      let overlayNode = document.createElement('div');
      overlayNode.className = 'popup-overlay';
      overlayNode.style.position = 'fixed';
      overlayNode.style.width = '100%';
      overlayNode.style.height = '100%';
      overlayNode.style.zIndex = '999';
      overlayNode.style.backgroundColor = 'rgba(0, 0, 0, 0.6)';
      overlayNode.style.top = '0px';
      overlayNode.style.left = '0px';
      this.document.querySelector('body').appendChild(overlayNode);
      this._overlayNode = overlayNode;
    }
  }

  // Open Popup Window
  public open(title, message, options?: IPopupOptions): Popup {
    let popup = new Popup(title, message, options);
    this.createPopup(popup, options).then((ob: any) => {
      let component = ob.componentRef;
      component.instance.popup = popup;
      component.instance.onActionClicked = () => {
        this.destroy(ob);
      };
      popup._component = component;
    });
    return popup;
  }

  // Destroy Popup Window
  private destroy(ob: any): void {
    ob.componentRef.destroy();
    if (this._overlayNode) {
      this._overlayRemoveTimeout = setTimeout(() => {
        this._overlayNode.remove();
        this._overlayNode = void 0;
      }, 600);
    }
  }
}
